import { Eye, Globe, Smile, Users } from 'lucide-react';
import { useState } from 'react';

import { AdminPage } from '@/components/admin/AdminPage';
import { RankedBars, TimeBars, type ChartSeries } from '@/components/admin/charts';
import { ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { KpiCard } from '@/components/ui/kpi-card';
import { useAnalytics } from '@/hooks/analytics';
import { formatDay } from '@/lib/dates';
import { formatCents } from '@shared/money';
import type { AnalyticsOverview } from '@shared/schemas/analytics';

/*
 * /admin/analytics — owner ask, 2026-08-10.
 *
 * **Two halves, and the screen never lets them be mistaken for each other.**
 *
 * The business half — revenue, where enquiries come from, how satisfied
 * customers are — is drawn from tables the office has been filling in by doing
 * its job. Every figure on it was true before this screen existed and covers the
 * whole history.
 *
 * The website half is new. **Counting started the day this shipped**, so a
 * "last 30 days" traffic figure covers a period that is mostly before anything
 * was counting. That is why the traffic section leads with the date it started
 * rather than tucking it in a footnote: a chart that begins at zero because
 * nothing was recording looks exactly like a website nobody visited, and only
 * that sentence tells them apart. It is the same defect as P1 — a figure
 * carrying more confidence than the data behind it.
 *
 * ---
 *
 * **This screen does not invent a revenue number.** Invoiced and collected are
 * the same two figures the Reports page totals, on the same two date columns,
 * and they are never netted into one. `report-service.ts` carries the full
 * reasoning; the short version is that they reconcile against different things —
 * the invoices, and the bank — and the gap between the bars is what is still
 * owed.
 */

const MONTH_OPTIONS = [
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
  { value: '24', label: '24 months' },
];

/** Both series, in fixed order. Never cycled, never reassigned by rank. */
const MONEY_SERIES: ChartSeries[] = [
  { name: 'Invoiced', fill: 'bg-chart-1' },
  { name: 'Payments received', fill: 'bg-chart-2' },
];

const TRAFFIC_SERIES: ChartSeries[] = [{ name: 'Visits', fill: 'bg-chart-1' }];

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card sm:p-6">
      <h2 className="text-h3 text-ink-900">{title}</h2>
      {description ? <p className="mt-1 text-body text-ink-500">{description}</p> : null}
      <div className="mt-5">{children}</div>
    </section>
  );
}

export function AnalyticsPage() {
  const [months, setMonths] = useState('12');
  const analytics = useAnalytics(Number(months));

  if (analytics.isError) {
    return (
      <AdminPage title="Analytics">
        {/* A failed request is an error, never an empty state — P22. */}
        <ErrorState
          title="Could not load the analytics"
          hint={analytics.error.message}
          onRetry={() => void analytics.refetch()}
        />
      </AdminPage>
    );
  }

  const data = analytics.data;

  return (
    <AdminPage
      title="Analytics"
      description="How the business is doing, and how many people are finding the website."
    >
      <div className="space-y-6">
        <RevenueSection
          data={data}
          isLoading={analytics.isPending}
          months={months}
          onMonthsChange={setMonths}
        />
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <LeadSourceSection data={data} />
          <SatisfactionSection data={data} isLoading={analytics.isPending} />
        </div>
        <TrafficSection data={data} isLoading={analytics.isPending} />
      </div>
    </AdminPage>
  );
}

// ---------------------------------------------------------------------------

function RevenueSection({
  data,
  isLoading,
  months,
  onMonthsChange,
}: {
  data: AnalyticsOverview | undefined;
  isLoading: boolean;
  months: string;
  onMonthsChange: (value: string) => void;
}) {
  const revenue = data?.revenue ?? [];

  return (
    <Panel
      title="Money in, by month"
      description="Invoiced is what went out in the month. Payments received is what actually arrived. They will not match, and the gap is what is still owed."
    >
      <div className="mb-5">
        <FilterPills
          label="How many months of revenue to show"
          options={MONTH_OPTIONS}
          value={months}
          onChange={onMonthsChange}
        />
      </div>

      {isLoading ? (
        <div aria-busy="true" className="h-52 animate-pulse rounded-md bg-ink-100">
          <span className="sr-only">Loading revenue by month</span>
        </div>
      ) : (
        <>
          <TimeBars
            points={revenue.map((month) => ({
              label: month.label,
              // "Aug" — the year is in the tooltip and in the table.
              tick: month.label.slice(0, 3),
              values: [month.invoicedCents, month.collectedCents],
            }))}
            series={MONEY_SERIES}
            format={formatCents}
            emptyLabel="No invoices have been issued and no payments recorded in this period."
          />

          {/*
            **The table view, and it is not an accessibility box being ticked.**
            Reading a figure off a bar is estimating; this is the same data as
            numbers, for anybody who wants to total a column or compare two
            months that are not next to each other.
          */}
          <details className="mt-5 border-t border-ink-300 pt-4">
            <summary className="inline-flex min-h-touch cursor-pointer items-center text-label text-bark-700 underline underline-offset-4">
              Show these figures as a table
            </summary>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-body">
                <thead>
                  <tr className="bg-ink-100 text-left text-meta text-ink-700">
                    <th scope="col" className="px-3 py-2 font-medium">
                      Month
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      Invoiced
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      Payments received
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {revenue.map((month) => (
                    <tr key={month.month} className="border-b border-ink-300 last:border-0">
                      <th scope="row" className="px-3 py-2 text-left font-normal text-ink-900">
                        {month.label}
                      </th>
                      <td className="px-3 py-2 text-right tabular-nums text-ink-700">
                        {formatCents(month.invoicedCents)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-ink-700">
                        {formatCents(month.collectedCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function LeadSourceSection({ data }: { data: AnalyticsOverview | undefined }) {
  const sources = (data?.leadSources ?? []).filter((source) => source.total > 0);

  return (
    <Panel
      title="Where enquiries come from"
      description="Every lead the business has ever taken, by source, and how many of each became a customer."
    >
      {/*
        **A legend, because the bar is two colours.** Colour on its own never
        says what it means — the same rule the status badges follow. Without it
        the darker segment inside each bar is just a second colour somebody has
        to guess at.
      */}
      <ul className="mb-4 flex flex-wrap gap-x-5 gap-y-2">
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="size-3 shrink-0 rounded-sm bg-chart-1" />
          <span className="text-meta text-ink-700">Enquiries</span>
        </li>
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="size-3 shrink-0 rounded-sm bg-chart-2" />
          <span className="text-meta text-ink-700">Became customers</span>
        </li>
      </ul>

      {/*
        **Both numbers are spelled out.** Rendered as bare figures they read as
        one number — "3" beside "1" is "31" at a glance, which is what the screen
        actually showed on its first run.
      */}
      <RankedBars
        bars={sources.map((source) => ({
          label: source.label,
          value: source.total,
          inner: {
            value: source.converted,
            label: source.converted === 1 ? 'became a customer' : 'became customers',
          },
        }))}
        format={(value) => `${value.toLocaleString()} ${value === 1 ? 'enquiry' : 'enquiries'}`}
        emptyLabel="No enquiries have been recorded yet."
      />
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function SatisfactionSection({
  data,
  isLoading,
}: {
  data: AnalyticsOverview | undefined;
  isLoading: boolean;
}) {
  const satisfaction = data?.satisfaction;

  return (
    <Panel
      title="Customer satisfaction"
      description="The rating currently recorded against each active customer."
    >
      <KpiCard
        label="Average rating"
        value={satisfaction?.average ?? null}
        format={(value) => `${value.toFixed(1)} out of 5`}
        // Never a dash. A business nobody has rated has no average, and saying
        // so is the honest answer (P1, P10).
        unknownLabel="No customer has been rated yet"
        subLabel={
          satisfaction === undefined
            ? undefined
            : `${String(satisfaction.rated)} rated · ${String(satisfaction.unrated)} not yet rated`
        }
        icon={Smile}
        isLoading={isLoading}
        className="border-0 p-0 shadow-none"
      />

      {/*
        The spread is only drawn once there is one. With nothing rated the card
        above already says so, and a second "no ratings recorded" underneath it
        is the same sentence twice.
      */}
      {satisfaction !== undefined && satisfaction.rated > 0 ? (
        <div className="mt-5">
          <RankedBars
            bars={satisfaction.distribution
              .slice()
              // Five stars first — a list of ratings reads best downwards.
              .reverse()
              .map((entry) => ({
                label: `${String(entry.rating)} star${entry.rating === 1 ? '' : 's'}`,
                value: entry.clients,
              }))}
            format={(value) => `${String(value)} customer${value === 1 ? '' : 's'}`}
            emptyLabel="No ratings recorded yet."
          />
        </div>
      ) : null}

      {/*
        **Why there is no trend line here.** One rating is stored per customer
        and overwritten when it changes, so there is no history to plot — and
        drawing today's numbers as though they had always been true would be a
        chart with the confidence of a record behind it and nothing behind it.
        Saying so on the screen is cheaper than somebody asking for the trend and
        being told the same thing.
      */}
      <p className="mt-5 border-t border-ink-300 pt-4 text-meta text-ink-500">
        This is the picture right now, not a trend. Only each customer&rsquo;s current rating is
        kept, so there is no history to chart.
      </p>
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function TrafficSection({
  data,
  isLoading,
}: {
  data: AnalyticsOverview | undefined;
  isLoading: boolean;
}) {
  const traffic = data?.traffic;

  return (
    <Panel
      title="Website visitors"
      description="People reading the public website — the home, services, contact and careers pages. Staff using the portals are not counted."
    >
      {/*
        **The first thing on this panel, not a footnote.** Counting started when
        this screen shipped, so a thirty-day figure covers a period that is
        mostly before anything was recording. Without this line an empty chart
        reads as a website nobody visits.
      */}
      {traffic !== undefined ? (
        <p className="mb-5 rounded-md bg-honey-50 px-3 py-2 text-meta text-bark-900">
          {traffic.countingSince === null
            ? 'Nothing has been counted yet. Figures will appear here as people visit the website.'
            : `Counting visits since ${formatDay(traffic.countingSince)}. Anything before that date was not recorded.`}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label="Today"
          value={traffic?.today.visitors ?? null}
          subLabel={
            traffic === undefined ? undefined : `${traffic.today.views.toLocaleString()} page views`
          }
          icon={Users}
          isLoading={isLoading}
        />
        <KpiCard
          label="Last 7 days"
          value={traffic?.last7.visitors ?? null}
          subLabel={
            traffic === undefined ? undefined : `${traffic.last7.views.toLocaleString()} page views`
          }
          icon={Eye}
          isLoading={isLoading}
        />
        <KpiCard
          label="Last 30 days"
          value={traffic?.last30.visitors ?? null}
          subLabel={
            traffic === undefined
              ? undefined
              : `${traffic.last30.views.toLocaleString()} page views`
          }
          icon={Globe}
          isLoading={isLoading}
        />
      </div>

      <div className="mt-6">
        <h3 className="text-label text-ink-900">Visits a day, last 30 days</h3>
        <div className="mt-3">
          {isLoading ? (
            <div aria-busy="true" className="h-48 animate-pulse rounded-md bg-ink-100">
              <span className="sr-only">Loading daily visits</span>
            </div>
          ) : (
            <TimeBars
              points={(traffic?.daily ?? []).map((day) => ({
                label: formatDay(day.day),
                // Day of the month. Thirty full dates will not fit and do not
                // need to — the tooltip carries the whole one.
                tick: day.day.slice(8),
                values: [day.views],
              }))}
              series={TRAFFIC_SERIES}
              format={(value) => `${String(value)} view${value === 1 ? '' : 's'}`}
              emptyLabel="No visits recorded yet."
            />
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <h3 className="text-label text-ink-900">Most-read pages</h3>
          <div className="mt-3">
            <RankedBars
              bars={(traffic?.topPages ?? []).map((page) => ({
                label: page.path,
                value: page.views,
              }))}
              format={(value) => value.toLocaleString()}
              emptyLabel="No page views recorded yet."
            />
          </div>
        </div>

        <div>
          <h3 className="text-label text-ink-900">Where they came from</h3>
          <div className="mt-3">
            <RankedBars
              bars={(traffic?.topReferrers ?? []).map((referrer) => ({
                // Null is somebody who typed the address or followed a bookmark,
                // which is a real and interesting answer rather than a gap.
                label: referrer.host ?? 'Typed the address',
                value: referrer.views,
              }))}
              format={(value) => value.toLocaleString()}
              emptyLabel="Nobody has arrived from another website yet."
            />
          </div>
        </div>
      </div>
    </Panel>
  );
}
